/**
 * Runtime map delivery — the state a client keeps about its map bundles
 * (spec `docs/superpowers/specs/2026-10-13-metro-v2-map-delivery-design.md`
 * §3.4–3.7). A port of `metro-server` `backend/pkg/mapcore/delivery`; the
 * rules are pinned scenario by scenario in `map-delivery-scenarios.json`, a
 * byte-for-byte copy of `metro-server/docs/map-delivery-scenarios.json` that
 * iOS and Android run too.
 */

/** Where a bundle lives: shipped with the site (`public/map-v2/`) or fetched at runtime. */
export type BundleSource = 'bundled' | 'downloaded'

/** One map bundle as the client sees it. */
export interface DeliveryBundle {
  readonly source: BundleSource
  readonly sequence: number
  readonly format: number
  /** sha256 of the bundle's `bundle.json` bytes — its identity (manifest `id`). */
  readonly manifest: string
  /** Every file present with the expected size. */
  readonly complete: boolean
}

/** Everything the client persists about its bundles. */
export interface DeliveryState {
  readonly bundled: DeliveryBundle
  /** What the last launch used. */
  readonly active?: DeliveryBundle
  /** Previous active that reached a first frame. */
  readonly lastGood?: DeliveryBundle
  /** Downloaded and verified, used from the next launch. */
  readonly pending?: DeliveryBundle
  /** Every downloaded bundle on disk. */
  readonly onDisk: readonly DeliveryBundle[]
  /** Manifests never to use again. */
  readonly bad: readonly string[]
  /** The active bundle reached a first map frame. */
  readonly activeConfirmed: boolean
  /** Manifest of a launch that has not reached its first frame yet. */
  readonly marker?: string
  /** Launches in a row that missed their first frame on `marker`. */
  readonly failures: number
  /** The remote bundle whose downloads keep failing verification. */
  readonly failedManifest?: string
  /** Failed downloads of `failedManifest` in a row. */
  readonly failedCount: number
}

/** An analytics event a decision emits (spec §3.8). */
export interface DeliveryEvent {
  readonly name: string
  readonly sequence?: number
  readonly from?: number
  readonly to?: number
  readonly reason?: string
}

/** The state of a client that has only the bundled map. */
export function initialDeliveryState(bundled: DeliveryBundle): DeliveryState {
  return { bundled, onDisk: [], bad: [], activeConfirmed: false, failures: 0, failedCount: 0 }
}

export function isBadManifest(state: DeliveryState, manifest: string): boolean {
  return state.bad.includes(manifest)
}
